/**
 * PersonaCard Component
 * Displays and allows editing of individual persona cards
 */
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Edit2, RefreshCw, Trash2, Save, X, Check } from 'lucide-react';
import { useModels } from '../hooks/ModelsContext';
import { useTranslation } from '../i18n/LanguageContext';

export function PersonaCard({
  persona,
  onUpdate,
  onRemove,
  onRegenerate,
  isEditable = true,
  isCompact = false,
}) {
  const { t } = useTranslation();
  const { models: MODELS } = useModels();
  const [isEditing, setIsEditing] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [editedPersona, setEditedPersona] = useState(persona);

  const model = MODELS[persona.model_id] || { name: persona.model_id, color: '#888' };

  const handleSave = () => {
    onUpdate(persona.id, editedPersona);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedPersona(persona);
    setIsEditing(false);
  };

  const handleRegenerate = async () => {
    setIsRegenerating(true);
    await onRegenerate(persona.id);
    setIsRegenerating(false);
  };

  if (isCompact) {
    return (
      <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-stone-50">
        <div
          className="w-3 h-3 rounded-full flex-shrink-0"
          style={{ backgroundColor: model.color }}
        />
        <span className="font-medium text-sm">{persona.name}</span>
        <span className="text-xs text-stone-400">{persona.age} {t('persona.years')}</span>
      </div>
    );
  }

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-6 bg-gradient-to-br from-stone-50 to-white rounded-xl border border-stone-200 relative group"
    >
      {/* Action Buttons */}
      {isEditable && !isEditing && (
        <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={() => setIsEditing(true)}
            className="p-1.5 text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded"
            title={t('persona.edit')}
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={handleRegenerate}
            disabled={isRegenerating}
            className="p-1.5 text-stone-400 hover:text-amber-600 hover:bg-amber-50 rounded"
            title={t('persona.regenerate')}
          >
            <RefreshCw className={`w-4 h-4 ${isRegenerating ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => onRemove(persona.id)}
            className="p-1.5 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded"
            title={t('persona.remove')}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )}

      {isEditing ? (
        /* Edit Mode */
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <input
              type="text"
              value={editedPersona.name}
              onChange={(e) => setEditedPersona({ ...editedPersona, name: e.target.value })}
              className="input text-lg font-medium"
              placeholder={t('persona.namePlaceholder')}
            />
            <div className="flex gap-1">
              <button
                onClick={handleSave}
                className="p-1.5 text-green-600 hover:bg-green-50 rounded"
              >
                <Check className="w-4 h-4" />
              </button>
              <button
                onClick={handleCancel}
                className="p-1.5 text-stone-400 hover:bg-stone-100 rounded"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-stone-500">{t('persona.ageLabel')}</label>
              <input
                type="number"
                value={editedPersona.age}
                onChange={(e) => setEditedPersona({ ...editedPersona, age: parseInt(e.target.value) || 0 })}
                className="input w-full"
                min="18"
                max="100"
              />
            </div>
            <div>
              <label className="text-xs text-stone-500">{t('persona.genderLabel')}</label>
              <select
                value={editedPersona.gender}
                onChange={(e) => setEditedPersona({ ...editedPersona, gender: e.target.value })}
                className="input w-full"
              >
                <option value="Man">{t('persona.genderMale')}</option>
                <option value="Kvinna">{t('persona.genderFemale')}</option>
                <option value="Annat">{t('persona.genderOther')}</option>
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs text-stone-500">{t('persona.regionLabel')}</label>
            <input
              type="text"
              value={editedPersona.region}
              onChange={(e) => setEditedPersona({ ...editedPersona, region: e.target.value })}
              className="input w-full"
            />
          </div>

          <div>
            <label className="text-xs text-stone-500">{t('persona.employmentLabel')}</label>
            <input
              type="text"
              value={editedPersona.employment}
              onChange={(e) => setEditedPersona({ ...editedPersona, employment: e.target.value })}
              className="input w-full"
            />
          </div>

          <div>
            <label className="text-xs text-stone-500">{t('persona.educationLabel')}</label>
            <input
              type="text"
              value={editedPersona.education}
              onChange={(e) => setEditedPersona({ ...editedPersona, education: e.target.value })}
              className="input w-full"
            />
          </div>

          <div>
            <label className="text-xs text-stone-500">{t('persona.traitsLabel')}</label>
            <input
              type="text"
              value={editedPersona.personality_traits?.join(', ') || ''}
              onChange={(e) => setEditedPersona({
                ...editedPersona,
                personality_traits: e.target.value.split(',').map(t => t.trim()).filter(t => t)
              })}
              className="input w-full"
            />
          </div>

          <div>
            <label className="text-xs text-stone-500">{t('report.colModel')}</label>
            <select
              value={editedPersona.model_id}
              onChange={(e) => setEditedPersona({ ...editedPersona, model_id: e.target.value })}
              className="input w-full"
            >
              {Object.entries(MODELS).map(([id, m]) => (
                <option key={id} value={id}>{m.name} ({m.provider})</option>
              ))}
            </select>
          </div>
        </div>
      ) : (
        /* View Mode */
        <>
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="font-medium text-lg text-stone-900">{persona.name}</h3>
              <p className="text-stone-500">{persona.age} {t('persona.years')}, {persona.gender}</p>
            </div>
            <select
              value={persona.model_id}
              onChange={(e) => onUpdate(persona.id, { model_id: e.target.value })}
              className="text-xs rounded-full px-2 py-1 text-white border-0 cursor-pointer appearance-none pr-6"
              style={{ backgroundColor: model.color, backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 6px center' }}
            >
              {Object.entries(MODELS).map(([id, m]) => (
                <option key={id} value={id} style={{ color: '#000', backgroundColor: '#fff' }}>{m.name}</option>
              ))}
            </select>
          </div>

          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-stone-400">📍</span>
              <span className="text-stone-600">{persona.region}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-stone-400">🎓</span>
              <span className="text-stone-600">{persona.education}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-stone-400">💼</span>
              <span className="text-stone-600">{persona.employment}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-stone-400">👨‍👩‍👧</span>
              <span className="text-stone-600">{persona.family_status}</span>
            </div>
          </div>

          {persona.personality_traits && persona.personality_traits.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {persona.personality_traits.map((trait, idx) => (
                <span key={idx} className="tag">{trait}</span>
              ))}
            </div>
          )}

          {persona.background_story && (
            <p className="mt-4 text-sm text-stone-600 italic border-t border-stone-100 pt-4">
              "{persona.background_story}"
            </p>
          )}
        </>
      )}
    </motion.div>
  );
}

export default PersonaCard;
